import { useState, useMemo, useRef, useEffect, useCallback } from "react"
import { Box, HStack, Input, Spinner, Stack, Table } from "@chakra-ui/react"
import { FaSort, FaSortUp, FaSortDown, FaDownload, FaExternalLinkAlt } from "react-icons/fa"

import { Section, Text, toaster } from "@/components"
import { useEmployeeOutreach, useOutreachCompanies } from "@/hooks/employeeOutreach"
import { downloadEmployeeOutreachCsv } from "@/api/employeeOutreach"
import type { EmployeeOutreachParams, EmployeeOutreachRow } from "@/api/employeeOutreach"

// ── Constants ────────────────────────────────────────────────────────────────

const JOB_TYPES = ["engineering", "marketing", "sales", "operations"] as const
const SENIORITY_TIERS = ["entry", "mid", "senior", "manager", "director", "vp", "c-suite"] as const

const JOB_TYPE_LABELS: Record<string, string> = {
  engineering: "Engineering",
  marketing: "Marketing",
  sales: "Sales",
  operations: "Operations",
}

const SENIORITY_LABELS: Record<string, string> = {
  entry: "Entry",
  mid: "Mid",
  senior: "Senior",
  manager: "Manager",
  director: "Director",
  vp: "VP",
  "c-suite": "C-Suite",
}

const STATUS_BADGE: Record<string, { bg: string; color: string }> = {
  current: { bg: "rgba(34,197,94,0.15)", color: "#22c55e" },
  departed: { bg: "rgba(156,163,175,0.2)", color: "#9ca3af" },
}

const SENIORITY_COLORS: Record<string, string> = {
  "c-suite": "#ef4444",
  vp: "#f59e0b",
  director: "#3b82f6",
  manager: "#8b5cf6",
  senior: "#06b6d4",
  mid: "#6b7280",
  entry: "#9ca3af",
}

type SortKey = keyof EmployeeOutreachRow
type SortDir = "asc" | "desc"

const COLS: { key: SortKey; label: string; align: "left" | "right" }[] = [
  { key: "name", label: "Name", align: "left" },
  { key: "linkedinUrl", label: "LinkedIn", align: "left" },
  { key: "companyName", label: "Company", align: "left" },
  { key: "jobTitle", label: "Job Title", align: "left" },
  { key: "startDate", label: "Start", align: "left" },
  { key: "endDate", label: "End", align: "left" },
  { key: "status", label: "Status", align: "left" },
  { key: "seniorityTier", label: "Seniority", align: "left" },
  { key: "jobType", label: "Job Type", align: "left" },
  { key: "email", label: "Email", align: "left" },
  { key: "location", label: "Location", align: "left" },
  { key: "tenureMonths", label: "Tenure", align: "right" },
]

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatTenure(months: number | null): string {
  if (months == null) return "-"
  const y = Math.floor(months / 12)
  const m = months % 12
  if (y === 0) return `${m}mo`
  if (m === 0) return `${y}y`
  return `${y}y ${m}mo`
}

const SortIcon = ({ isActive, dir }: { isActive: boolean; dir: SortDir }) => {
  if (!isActive) return <FaSort style={{ opacity: 0.3, fontSize: "10px" }} />
  return dir === "asc" ? (
    <FaSortUp style={{ fontSize: "10px" }} />
  ) : (
    <FaSortDown style={{ fontSize: "10px" }} />
  )
}

// ── Company Selector ─────────────────────────────────────────────────────────

function CompanySelector({
  selectedIds,
  onChange,
  disabled,
  showSelected,
  setShowSelected,
}: {
  selectedIds: number[]
  onChange: (ids: number[]) => void
  disabled: boolean
  showSelected: boolean
  setShowSelected: (v: boolean) => void
}) {
  const { data: companies } = useOutreachCompanies()
  const [search, setSearch] = useState("")
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  // Auto-exit "show selected" mode when last company is removed
  useEffect(() => {
    if (showSelected && selectedIds.length === 0) setShowSelected(false)
  }, [showSelected, selectedIds.length, setShowSelected])

  // Clear search and open dropdown when toggling mode
  useEffect(() => {
    setSearch("")
    if (showSelected && selectedIds.length > 0) setOpen(true)
  }, [showSelected]) // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!companies) return []
    const q = search.toLowerCase()

    if (showSelected) {
      const selectedSet = new Set(selectedIds)
      const selected = companies.filter((c) => selectedSet.has(c.id))
      if (!q) return selected
      return selected.filter((c) => c.name.toLowerCase().includes(q))
    }

    if (!q) return companies.slice(0, 100)
    return companies.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 100)
  }, [companies, search, showSelected, selectedIds])

  const toggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id))
    } else {
      onChange([...selectedIds, id])
    }
  }

  return (
    <Box ref={ref} position="relative" minW="240px" maxW="320px">
      <Input
        size="sm"
        placeholder={
          disabled
            ? "Using Hotlist"
            : showSelected
            ? `Filter selected (${selectedIds.length})`
            : `Search companies (${selectedIds.length} selected)`
        }
        value={search}
        onChange={(e) => {
          setSearch(e.target.value)
          setOpen(true)
        }}
        onFocus={() => !disabled && setOpen(true)}
        disabled={disabled}
        borderColor={showSelected ? "#3b82f6" : "border.default"}
      />
      {open && !disabled && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          bg="card.bg"
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="md"
          maxH="300px"
          overflowY="auto"
          zIndex={10}
          mt={1}
        >
          {/* Header when in "show selected" mode */}
          {showSelected && selectedIds.length > 0 && (
            <HStack
              px={3}
              py={1.5}
              justifyContent="space-between"
              borderBottomWidth="1px"
              borderColor="border.default"
              bg="rgba(59,130,246,0.05)"
            >
              <Text fontSize="xs" color="text.muted" fontWeight="600">
                {filtered.length} selected
              </Text>
              <Box
                as="button"
                fontSize="xs"
                color="#ef4444"
                fontWeight="500"
                _hover={{ textDecoration: "underline" }}
                onClick={() => {
                  onChange([])
                  setShowSelected(false)
                  setSearch("")
                  setOpen(false)
                }}
              >
                Clear all
              </Box>
            </HStack>
          )}

          {filtered.length === 0 ? (
            <Box px={3} py={2}>
              <Text fontSize="xs" color="text.muted">
                {showSelected ? "No selected companies match" : "No companies found"}
              </Text>
            </Box>
          ) : (
            filtered.map((c) => (
              <HStack
                key={c.id}
                px={3}
                py={1}
                cursor="pointer"
                _hover={{ bg: "hover.bg" }}
                onClick={() => toggle(c.id)}
                justifyContent="space-between"
              >
                <HStack gap={2}>
                  {!showSelected && (
                    <Box
                      w="16px"
                      h="16px"
                      borderWidth="1px"
                      borderRadius="sm"
                      borderColor="border.default"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {selectedIds.includes(c.id) && (
                        <Box w="10px" h="10px" borderRadius="sm" bg="#3b82f6" />
                      )}
                    </Box>
                  )}
                  <Text fontSize="xs">{c.name}</Text>
                </HStack>
                {showSelected && (
                  <Text fontSize="xs" color="text.muted" _hover={{ color: "#ef4444" }}>
                    x
                  </Text>
                )}
              </HStack>
            ))
          )}
        </Box>
      )}
    </Box>
  )
}

// ── Toggle Chip ──────────────────────────────────────────────────────────────

function ToggleChip({
  label,
  active,
  onClick,
  color,
}: {
  label: string
  active: boolean
  onClick: () => void
  color?: string
}) {
  return (
    <Box
      as="button"
      px={2}
      py={1}
      borderRadius="md"
      fontSize="xs"
      fontWeight="600"
      cursor="pointer"
      bg={active ? (color ? `${color}22` : "rgba(59,130,246,0.15)") : "transparent"}
      color={active ? (color || "#3b82f6") : "text.muted"}
      borderWidth="1px"
      borderColor={active ? (color || "#3b82f6") : "border.default"}
      onClick={onClick}
      _hover={{ bg: active ? undefined : "hover.bg" }}
    >
      {label}
    </Box>
  )
}

// ── Main Component ───────────────────────────────────────────────────────────

export const EmployeeOutreachComponent = () => {
  // Filter state
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([])
  const [useHotlist, setUseHotlist] = useState(false)
  const [showSelectedCompanies, setShowSelectedCompanies] = useState(false)
  const [statusFilter, setStatusFilter] = useState<"current" | "departed" | "">("")
  const [jobTypeFilter, setJobTypeFilter] = useState<string[]>([])
  const [seniorityFilter, setSeniorityFilter] = useState<string[]>([])
  const [startDateFrom, setStartDateFrom] = useState("")
  const [startDateTo, setStartDateTo] = useState("")
  const [endDateFrom, setEndDateFrom] = useState("")
  const [endDateTo, setEndDateTo] = useState("")
  const [tenureMinMonths, setTenureMinMonths] = useState("")
  const [tenureMaxMonths, setTenureMaxMonths] = useState("")
  const [emailFilter, setEmailFilter] = useState<"" | "yes" | "no">("")
  const [nameSearch, setNameSearch] = useState("")
  const [emailSearch, setEmailSearch] = useState("")

  // Pagination + sort
  const [page, setPage] = useState(1)
  const pageSize = 100
  const [sortKey, setSortKey] = useState<SortKey>("name")
  const [sortDir, setSortDir] = useState<SortDir>("asc")

  // Search trigger — always requires clicking Search
  const [activeParams, setActiveParams] = useState<EmployeeOutreachParams | null>(null)

  const hasSelection = useHotlist || selectedCompanyIds.length > 0 || nameSearch.trim().length > 0 || emailSearch.trim().length > 0

  const buildParams = useCallback(
    (p: number): EmployeeOutreachParams => ({
      ...(useHotlist
        ? { source: "hotlist" as const }
        : selectedCompanyIds.length > 0
        ? { companyIds: selectedCompanyIds }
        : {}),
      ...(nameSearch.trim() && { name: nameSearch.trim() }),
      ...(emailSearch.trim() && { email: emailSearch.trim() }),
      ...(statusFilter && { status: statusFilter as "current" | "departed" }),
      ...(jobTypeFilter.length > 0 && { jobType: jobTypeFilter }),
      ...(seniorityFilter.length > 0 && { seniority: seniorityFilter }),
      ...(startDateFrom && { startDateFrom }),
      ...(startDateTo && { startDateTo }),
      ...(endDateFrom && { endDateFrom }),
      ...(endDateTo && { endDateTo }),
      ...(tenureMinMonths && { tenureMinMonths: parseInt(tenureMinMonths) }),
      ...(tenureMaxMonths && { tenureMaxMonths: parseInt(tenureMaxMonths) }),
      ...(emailFilter && { hasEmail: emailFilter as "yes" | "no" }),
      page: p,
      pageSize,
    }),
    [useHotlist, selectedCompanyIds, nameSearch, emailSearch, statusFilter, jobTypeFilter, seniorityFilter, startDateFrom, startDateTo, endDateFrom, endDateTo, tenureMinMonths, tenureMaxMonths, emailFilter]
  )

  // Detect if filters have changed since last search
  const filtersChanged = useMemo(() => {
    if (!activeParams) return false
    const pending = buildParams(1)
    // Compare everything except page
    const { page: _ap, ...activeRest } = activeParams
    const { page: _pp, ...pendingRest } = pending
    return JSON.stringify(activeRest) !== JSON.stringify(pendingRest)
  }, [activeParams, buildParams])

  const handleSearch = () => {
    if (!hasSelection) return
    setPage(1)
    setActiveParams(buildParams(1))
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
    setActiveParams(buildParams(newPage))
  }

  const { data, isLoading } = useEmployeeOutreach(
    activeParams ?? { page: 1, pageSize: 100 },
    activeParams != null
  )

  // Client-side sorting
  const sortedRows = useMemo(() => {
    if (!data?.rows) return []
    const rows = [...data.rows]
    rows.sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      const cmp =
        typeof av === "string" && typeof bv === "string"
          ? av.localeCompare(bv)
          : typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv))
      return sortDir === "asc" ? cmp : -cmp
    })
    return rows
  }, [data?.rows, sortKey, sortDir])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir("asc")
    }
  }

  const toggleJobType = (jt: string) => {
    setJobTypeFilter((prev) =>
      prev.includes(jt) ? prev.filter((x) => x !== jt) : [...prev, jt]
    )
  }

  const toggleSeniority = (s: string) => {
    setSeniorityFilter((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    )
  }

  const [exporting, setExporting] = useState(false)
  const handleExport = async () => {
    if (!activeParams) return
    setExporting(true)
    try {
      await downloadEmployeeOutreachCsv(activeParams)
      toaster.create({ title: "CSV downloaded", type: "success", duration: 3000 })
    } catch (e: any) {
      toaster.create({ title: "Export failed", description: e.message, type: "error" })
    } finally {
      setExporting(false)
    }
  }

  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 0
  const rangeStart = data ? (data.page - 1) * data.pageSize + 1 : 0
  const rangeEnd = data ? Math.min(data.page * data.pageSize, data.total) : 0

  return (
    <Section>
      <Stack gap={4} w="100%">
        {/* Header */}
        <HStack justifyContent="space-between" alignItems="center">
          <Text fontSize="xl" fontWeight="bold">
            Employee Outreach
          </Text>
          {activeParams && (
            <Box
              as="button"
              px={3}
              py={1.5}
              borderRadius="md"
              fontSize="xs"
              fontWeight="600"
              bg={exporting ? "rgba(59,130,246,0.1)" : "rgba(59,130,246,0.15)"}
              color="#3b82f6"
              cursor={exporting ? "default" : "pointer"}
              onClick={handleExport}
              disabled={exporting}
              _hover={{ bg: "rgba(59,130,246,0.25)" }}
            >
              <HStack gap={1}>
                {exporting ? <Spinner size="xs" /> : <FaDownload style={{ fontSize: "10px" }} />}
                <Text fontSize="xs">Download CSV</Text>
              </HStack>
            </Box>
          )}
        </HStack>

        {/* Filters */}
        <Stack gap={3} p={4} borderWidth="1px" borderColor="border.default" borderRadius="md" bg="card.bg">
          {/* Row 1: Company + Hotlist + Status */}
          <HStack gap={3} flexWrap="wrap" alignItems="flex-end">
            <Stack gap={1}>
              <HStack gap={2} alignItems="baseline">
                <Text fontSize="xs" fontWeight="600" color="text.muted">
                  Companies
                </Text>
                {!useHotlist && selectedCompanyIds.length > 0 && (
                  <Box
                    as="button"
                    fontSize="2xs"
                    color={showSelectedCompanies ? "#3b82f6" : "text.muted"}
                    fontWeight="500"
                    _hover={{ color: "#3b82f6" }}
                    onClick={() => setShowSelectedCompanies((v) => !v)}
                  >
                    {showSelectedCompanies ? "Add Companies" : `Show Selected (${selectedCompanyIds.length})`}
                  </Box>
                )}
              </HStack>
              <HStack gap={2}>
                <CompanySelector
                  selectedIds={selectedCompanyIds}
                  onChange={setSelectedCompanyIds}
                  disabled={useHotlist}
                  showSelected={showSelectedCompanies}
                  setShowSelected={setShowSelectedCompanies}
                />
                <ToggleChip
                  label="Hotlist"
                  active={useHotlist}
                  onClick={() => setUseHotlist((v) => !v)}
                  color="#f59e0b"
                />
              </HStack>
            </Stack>

            <Stack gap={1}>
              <Text fontSize="xs" fontWeight="600" color="text.muted">
                Status
              </Text>
              <HStack gap={1}>
                {(["", "current", "departed"] as const).map((s) => (
                  <ToggleChip
                    key={s || "all"}
                    label={s === "" ? "All" : s === "current" ? "Current" : "Departed"}
                    active={statusFilter === s}
                    onClick={() => setStatusFilter(s)}
                  />
                ))}
              </HStack>
            </Stack>

            <Stack gap={1}>
              <Text fontSize="xs" fontWeight="600" color="text.muted">
                Email
              </Text>
              <HStack gap={1}>
                {(["", "yes", "no"] as const).map((v) => (
                  <ToggleChip
                    key={v || "all"}
                    label={v === "" ? "All" : v === "yes" ? "Has Email" : "No Email"}
                    active={emailFilter === v}
                    onClick={() => setEmailFilter(v)}
                    color={v === "yes" ? "#22c55e" : v === "no" ? "#ef4444" : undefined}
                  />
                ))}
              </HStack>
            </Stack>
          </HStack>

          {/* Row 2: Name + Email search */}
          <HStack gap={3} flexWrap="wrap" alignItems="flex-end">
            <Stack gap={1}>
              <Text fontSize="xs" fontWeight="600" color="text.muted">
                Name
              </Text>
              <Input
                size="sm"
                maxW="220px"
                placeholder="Search by name"
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && hasSelection && handleSearch()}
                borderColor="border.default"
              />
            </Stack>
            <Stack gap={1}>
              <Text fontSize="xs" fontWeight="600" color="text.muted">
                Email
              </Text>
              <Input
                size="sm"
                maxW="260px"
                placeholder="Search by email"
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && hasSelection && handleSearch()}
                borderColor="border.default"
              />
            </Stack>
          </HStack>

          {/* Row 3: Job Type + Seniority */}
          <HStack gap={4} flexWrap="wrap">
            <Stack gap={1}>
              <Text fontSize="xs" fontWeight="600" color="text.muted">
                Job Type
              </Text>
              <HStack gap={1} flexWrap="wrap">
                {JOB_TYPES.map((jt) => (
                  <ToggleChip
                    key={jt}
                    label={JOB_TYPE_LABELS[jt]}
                    active={jobTypeFilter.includes(jt)}
                    onClick={() => toggleJobType(jt)}
                  />
                ))}
              </HStack>
            </Stack>

            <Stack gap={1}>
              <Text fontSize="xs" fontWeight="600" color="text.muted">
                Seniority
              </Text>
              <HStack gap={1} flexWrap="wrap">
                {SENIORITY_TIERS.map((s) => (
                  <ToggleChip
                    key={s}
                    label={SENIORITY_LABELS[s]}
                    active={seniorityFilter.includes(s)}
                    onClick={() => toggleSeniority(s)}
                    color={SENIORITY_COLORS[s]}
                  />
                ))}
              </HStack>
            </Stack>
          </HStack>

          {/* Row 3: Date range + Tenure + Search */}
          <HStack gap={3} flexWrap="wrap" alignItems="flex-end">
            <Stack gap={1}>
              <Text fontSize="xs" fontWeight="600" color="text.muted">
                Start Date From
              </Text>
              <Input
                type="date"
                size="sm"
                maxW="160px"
                value={startDateFrom}
                onChange={(e) => setStartDateFrom(e.target.value)}
                borderColor="border.default"
              />
            </Stack>
            <Stack gap={1}>
              <Text fontSize="xs" fontWeight="600" color="text.muted">
                Start Date To
              </Text>
              <Input
                type="date"
                size="sm"
                maxW="160px"
                value={startDateTo}
                onChange={(e) => setStartDateTo(e.target.value)}
                borderColor="border.default"
              />
            </Stack>
            <Stack gap={1}>
              <Text fontSize="xs" fontWeight="600" color="text.muted">
                End Date From
              </Text>
              <Input
                type="date"
                size="sm"
                maxW="160px"
                value={endDateFrom}
                onChange={(e) => setEndDateFrom(e.target.value)}
                borderColor="border.default"
              />
            </Stack>
            <Stack gap={1}>
              <Text fontSize="xs" fontWeight="600" color="text.muted">
                End Date To
              </Text>
              <Input
                type="date"
                size="sm"
                maxW="160px"
                value={endDateTo}
                onChange={(e) => setEndDateTo(e.target.value)}
                borderColor="border.default"
              />
            </Stack>
            <Stack gap={1}>
              <Text fontSize="xs" fontWeight="600" color="text.muted">
                Min Tenure (months)
              </Text>
              <Input
                type="number"
                size="sm"
                maxW="120px"
                placeholder="0"
                value={tenureMinMonths}
                onChange={(e) => setTenureMinMonths(e.target.value)}
                borderColor="border.default"
              />
            </Stack>
            <Stack gap={1}>
              <Text fontSize="xs" fontWeight="600" color="text.muted">
                Max Tenure (months)
              </Text>
              <Input
                type="number"
                size="sm"
                maxW="120px"
                placeholder="any"
                value={tenureMaxMonths}
                onChange={(e) => setTenureMaxMonths(e.target.value)}
                borderColor="border.default"
              />
            </Stack>
            <Stack gap={1} alignItems="flex-start">
              <Box
                as="button"
                px={4}
                py={1.5}
                borderRadius="md"
                fontSize="sm"
                fontWeight="600"
                bg={hasSelection ? (filtersChanged ? "#f59e0b" : "#3b82f6") : "rgba(59,130,246,0.3)"}
                color="white"
                cursor={hasSelection ? "pointer" : "default"}
                onClick={handleSearch}
                _hover={hasSelection ? { bg: filtersChanged ? "#d97706" : "#2563eb" } : {}}
              >
                Search
              </Box>
              {filtersChanged && (
                <Text fontSize="xs" color="#f59e0b" fontWeight="500">
                  Filters changed — click Search to update
                </Text>
              )}
            </Stack>
          </HStack>
        </Stack>

        {/* Results */}
        {isLoading && (
          <HStack gap={2} py={4}>
            <Spinner size="sm" />
            <Text fontSize="sm" color="text.muted">
              Loading employees...
            </Text>
          </HStack>
        )}

        {data && !isLoading && (
          <>
            {/* Summary + Pagination */}
            <HStack justifyContent="space-between">
              <Text fontSize="xs" color="text.muted">
                Showing {rangeStart}–{rangeEnd} of {data.total.toLocaleString()} employees
              </Text>
              {totalPages > 1 && (
                <HStack gap={2}>
                  <Box
                    as="button"
                    px={2}
                    py={1}
                    borderRadius="md"
                    fontSize="xs"
                    cursor={page > 1 ? "pointer" : "default"}
                    color={page > 1 ? "#3b82f6" : "text.muted"}
                    onClick={() => page > 1 && handlePageChange(page - 1)}
                  >
                    Prev
                  </Box>
                  <Text fontSize="xs" color="text.muted">
                    Page {page} of {totalPages}
                  </Text>
                  <Box
                    as="button"
                    px={2}
                    py={1}
                    borderRadius="md"
                    fontSize="xs"
                    cursor={page < totalPages ? "pointer" : "default"}
                    color={page < totalPages ? "#3b82f6" : "text.muted"}
                    onClick={() => page < totalPages && handlePageChange(page + 1)}
                  >
                    Next
                  </Box>
                </HStack>
              )}
            </HStack>

            {/* Table */}
            <Box overflowX="auto" borderWidth="1px" borderColor="border.default" borderRadius="md">
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row bg="table.header">
                    {COLS.map((col) => (
                      <Table.ColumnHeader
                        key={col.key}
                        textAlign={col.align}
                        cursor="pointer"
                        onClick={() => handleSort(col.key)}
                        px={2}
                        py={2}
                        whiteSpace="nowrap"
                      >
                        <HStack
                          gap={1}
                          justifyContent={
                            col.align === "right" ? "flex-end" : "flex-start"
                          }
                        >
                          <Text fontSize="xs" fontWeight="semibold">
                            {col.label}
                          </Text>
                          <SortIcon isActive={sortKey === col.key} dir={sortDir} />
                        </HStack>
                      </Table.ColumnHeader>
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {sortedRows.length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan={COLS.length} textAlign="center" py={8}>
                        <Text color="text.muted" fontSize="sm">
                          No employees found matching your filters
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    sortedRows.map((row) => (
                      <Table.Row key={row.eventId} _hover={{ bg: "hover.bg" }}>
                        {/* Name */}
                        <Table.Cell px={2} py={1.5}>
                          <HStack gap={1}>
                            <Text fontSize="xs" fontWeight="500">
                              {row.name ?? "-"}
                            </Text>
                            {row.blocked && (
                              <Box
                                display="inline-block"
                                px={1.5}
                                py={0}
                                borderRadius="md"
                                bg="rgba(239,68,68,0.12)"
                              >
                                <Text fontSize="2xs" fontWeight="600" color="#ef4444">
                                  DNC
                                </Text>
                              </Box>
                            )}
                          </HStack>
                        </Table.Cell>
                        {/* LinkedIn */}
                        <Table.Cell px={2} py={1.5}>
                          {row.linkedinUrl ? (
                            <a
                              href={row.linkedinUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <FaExternalLinkAlt
                                style={{ fontSize: "10px", color: "#3b82f6" }}
                              />
                            </a>
                          ) : (
                            <Text fontSize="xs" color="text.muted">
                              -
                            </Text>
                          )}
                        </Table.Cell>
                        {/* Company */}
                        <Table.Cell px={2} py={1.5}>
                          <HStack gap={1}>
                            <Text fontSize="xs">{row.companyName}</Text>
                            {row.excludeMarketing && (
                              <Box
                                display="inline-block"
                                px={1.5}
                                py={0}
                                borderRadius="md"
                                bg="rgba(239,68,68,0.12)"
                              >
                                <Text fontSize="2xs" fontWeight="600" color="#ef4444">
                                  DNC
                                </Text>
                              </Box>
                            )}
                          </HStack>
                        </Table.Cell>
                        {/* Job Title */}
                        <Table.Cell px={2} py={1.5} maxW="200px">
                          <Text fontSize="xs" truncate>
                            {row.jobTitle ?? "-"}
                          </Text>
                        </Table.Cell>
                        {/* Start */}
                        <Table.Cell px={2} py={1.5}>
                          <Text fontSize="xs">{row.startDate ?? "-"}</Text>
                        </Table.Cell>
                        {/* End */}
                        <Table.Cell px={2} py={1.5}>
                          <Text fontSize="xs">{row.endDate ?? "-"}</Text>
                        </Table.Cell>
                        {/* Status */}
                        <Table.Cell px={2} py={1.5}>
                          <Box
                            display="inline-block"
                            px={2}
                            py={0.5}
                            borderRadius="md"
                            bg={STATUS_BADGE[row.status].bg}
                          >
                            <Text
                              fontSize="xs"
                              fontWeight="600"
                              color={STATUS_BADGE[row.status].color}
                            >
                              {row.status === "current" ? "Current" : "Departed"}
                            </Text>
                          </Box>
                        </Table.Cell>
                        {/* Seniority */}
                        <Table.Cell px={2} py={1.5}>
                          <Box
                            display="inline-block"
                            px={2}
                            py={0.5}
                            borderRadius="md"
                            bg={`${SENIORITY_COLORS[row.seniorityTier] ?? "#6b7280"}22`}
                          >
                            <Text
                              fontSize="xs"
                              fontWeight="600"
                              color={SENIORITY_COLORS[row.seniorityTier] ?? "#6b7280"}
                            >
                              {SENIORITY_LABELS[row.seniorityTier] ?? row.seniorityTier}
                            </Text>
                          </Box>
                        </Table.Cell>
                        {/* Job Type */}
                        <Table.Cell px={2} py={1.5}>
                          {row.jobType ? (
                            <Text fontSize="xs">
                              {JOB_TYPE_LABELS[row.jobType] ?? row.jobType}
                            </Text>
                          ) : (
                            <Text fontSize="xs" color="text.muted">
                              -
                            </Text>
                          )}
                        </Table.Cell>
                        {/* Email */}
                        <Table.Cell px={2} py={1.5}>
                          <Text fontSize="xs" truncate maxW="180px">
                            {row.email ?? "-"}
                          </Text>
                        </Table.Cell>
                        {/* Location */}
                        <Table.Cell px={2} py={1.5}>
                          <Text fontSize="xs" truncate maxW="150px">
                            {row.location ?? "-"}
                          </Text>
                        </Table.Cell>
                        {/* Tenure */}
                        <Table.Cell px={2} py={1.5} textAlign="right">
                          <Text fontSize="xs">{formatTenure(row.tenureMonths)}</Text>
                        </Table.Cell>
                      </Table.Row>
                    ))
                  )}
                </Table.Body>
              </Table.Root>
            </Box>

            {/* Bottom pagination */}
            {totalPages > 1 && (
              <HStack justifyContent="flex-end" pt={1}>
                <Box
                  as="button"
                  px={2}
                  py={1}
                  borderRadius="md"
                  fontSize="xs"
                  cursor={page > 1 ? "pointer" : "default"}
                  color={page > 1 ? "#3b82f6" : "text.muted"}
                  onClick={() => page > 1 && handlePageChange(page - 1)}
                >
                  Prev
                </Box>
                <Text fontSize="xs" color="text.muted">
                  Page {page} of {totalPages}
                </Text>
                <Box
                  as="button"
                  px={2}
                  py={1}
                  borderRadius="md"
                  fontSize="xs"
                  cursor={page < totalPages ? "pointer" : "default"}
                  color={page < totalPages ? "#3b82f6" : "text.muted"}
                  onClick={() => page < totalPages && handlePageChange(page + 1)}
                >
                  Next
                </Box>
              </HStack>
            )}
          </>
        )}

        {!activeParams && !isLoading && (
          <Box py={8} textAlign="center">
            <Text color="text.muted" fontSize="sm">
              Select companies, enable Hotlist, or search by name/email, then click Search.
            </Text>
          </Box>
        )}
      </Stack>
    </Section>
  )
}
